import React, { useState, useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Box, IconButton, Chip, Typography, Paper, CircularProgress, MenuItem, Tooltip, Menu, useTheme, useMediaQuery, Divider, DialogTitle, DialogContent, DialogActions, Button, ButtonGroup } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Mic, Send, AttachFile, BuildCircle, Close as CloseIcon, InfoOutlined, Psychology, ArrowDropDown } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { listConversations, updateConversation } from '@/api/conversations';
import { useAppStore } from '@/store';
import { modelsAPI } from '@/api/models';
import type { DictationLanguage } from '@/models/conversation';
import DialogWrapper from './DialogWrapper';
import SourceToggles from './SourceToggles';

// Declare SpeechRecognition types for TypeScript
declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

// Styled input container with box shadow and reduced width
const FloatingInput = styled(Paper)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: '8px 12px',
  background: theme.palette.mode === 'light' ? theme.palette.background.paper : theme.palette.background.paper,
  // maxWidth: '800px',
  width: '100%',
  margin: '0 auto',
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
  borderRadius: '12px',
  flexDirection: 'column',
  borderColor: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.1)' : 'rgba(255, 255, 255, 0.1)',
  borderWidth: '1px',
  borderStyle: 'solid',
}));

const ALLOWED_FILE_TYPES = {
  documents: ['.pdf', '.docx', '.doc', '.txt'],
  images: ['.png', '.jpg', '.jpeg', '.gif', '.webp']
};
const ALL_ALLOWED_TYPES = [...ALLOWED_FILE_TYPES.documents, ...ALLOWED_FILE_TYPES.images];
const MAX_FILE_SIZE = {
  documents: 20 * 1024 * 1024,  // 20MB
  images: 20 * 1024 * 1024      // 20MB
};
const MAX_FILES = 5;
const MAX_TOTAL_FILE_SIZE = 40 * 1024 * 1024; // 40MB total per message
const MESSAGE_ATTACHMENT_LIMITS_TEXT = `You reached a message attachment limit: up to ${MAX_FILES} files and up to ${(MAX_TOTAL_FILE_SIZE / (1024 * 1024)).toFixed(0)}MB total.`;
const DEFAULT_DICTATION_LANGUAGE: DictationLanguage = 'en-US';
const DICTATION_LANGUAGE_OPTIONS: Array<{ value: DictationLanguage; label: string }> = [
  { value: 'en-US', label: 'English (US)' },
  { value: 'de-DE', label: 'German (Germany)' },
  { value: 'fr-FR', label: 'French (France)' },
  { value: 'it-IT', label: 'Italian (Italy)' },
  { value: 'es-ES', label: 'Spanish (Spain)' },
  { value: 'da-DK', label: 'Danish (Denmark)' },
  { value: 'zh-CN', label: 'Chinese (Simplified, China)' },
  { value: 'zh-TW', label: 'Chinese (Traditional, Taiwan)' },
];

interface ChatInputProps {
  conversationId: string;
  onMessageSent: (conversationId: string) => void;
  onBeforeSend: (text: string, files: File[]) => void | Promise<void>;
  disabled?: boolean;
}

// Define the ref type
export interface ChatInputHandle {
  handleDroppedFiles: (files: File[]) => void;
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(({ 
  conversationId, 
  onMessageSent, 
  onBeforeSend,
  disabled = false
}, ref) => {
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [imagePreviewUrls, setImagePreviewUrls] = useState<Map<string, string>>(new Map());
  const [error, setError] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [allModels, setAllModels] = useState<{ id: string; name: string; supports_images?: boolean }[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [showMoreControls, setShowMoreControls] = useState(false);
  
  // Use message from global store instead of local state
  const message = useAppStore(s => s.message);
  const setMessage = useAppStore(s => s.setMessage);
  const [modelMenuAnchorEl, setModelMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [dictationMenuAnchorEl, setDictationMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const [modelToShowDescription, setModelToShowDescription] = useState<string | null>(null);
  const [dictationLanguage, setDictationLanguage] = useState<DictationLanguage>(DEFAULT_DICTATION_LANGUAGE);
  const recognitionRef = useRef<any>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const previousConversationIdRef = useRef(conversationId);
  const currentConversationIdRef = useRef(conversationId);
  const latestMessageRef = useRef('');
  const latestFilesRef = useRef<File[]>([]);
  const latestSendingRef = useRef(false);
  const unsentDraftsRef = useRef<Map<string, { message: string; files: File[] }>>(new Map());
  const selectedModel = useAppStore(s => s.selectedModel);
  const setSelectedModel = useAppStore(s => s.setSelectedModel);
  const thinkingMode = useAppStore(s => s.thinkingMode);
  const setThinkingMode = useAppStore(s => s.setThinkingMode);
  const modelList = useAppStore(s => s.modelList);
  // const selectedAgent = useAppStore(s => s.selectedAgent);
  const selectedAgent = useAppStore(s => s.selectedAgent);
  // Researcher agent uses a fixed model and always runs at maximum depth—hide both selectors.
  const isResearcher = selectedAgent === 'researcher';
  const setConversationList = useAppStore(s => s.setConversationList);
  const conversationList = useAppStore(s => s.conversationList);
  const selectedConversation = useAppStore(s => s.selectedConversation);
  const setSelectedConversation = useAppStore(s => s.setSelectedConversation);
  const messages = useAppStore(s => s.messages);
  const userSettings = useAppStore(s => s.userSettings);
  const [thinkingMenuAnchorEl, setThinkingMenuAnchorEl] = useState<null | HTMLElement>(null);

  const getDraftKey = (id: string) => id || '__new__';

  const useCompactControlRow = isSmallDevice;
  const canToggleMoreControls = useCompactControlRow && !isResearcher;

  const selectedModelSupportsImages = !!modelList.find(m => m.id === selectedModel)?.supports_images;
  const allowedFileTypesForSelectedModel = selectedModelSupportsImages
    ? ALL_ALLOWED_TYPES
    : ALLOWED_FILE_TYPES.documents;
  
  // Construct OneDrive folder URL based on user's UPN (User Principal Name)
  const getOneDriveFolderUrl = () => {
    if (!userSettings?.user_principal_name) {
      return 'https://bosnet-my.sharepoint.com/';
    }
    // Extract token from UPN: TOKEN@bossard.com -> token
    const upnToken = userSettings.user_principal_name.split('@')[0].toLowerCase();
    // URL-encode the path: /personal/{token}_bossard_com/Documents/Documents/Bossard Bolt/Files
    const encodedPath = encodeURIComponent(`/personal/${upnToken}_bossard_com/Documents/Documents/Bossard Bolt/Files`);
    return `https://bosnet-my.sharepoint.com/my?id=${encodedPath}`;
  };

  // Auto-grow textarea effect
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      const maxHeight = 200; // Max height before scrollbar appears
      textareaRef.current.style.height = `${Math.min(scrollHeight, maxHeight)}px`;
    }
  }, [message]);

  const getTotalFileSize = (fileList: File[]): number => {
    return fileList.reduce((totalSize, currentFile) => totalSize + currentFile.size, 0);
  };

  useEffect(() => {
    const selectedLanguage = selectedConversation?.dictation_language;
    const isSupportedLanguage = DICTATION_LANGUAGE_OPTIONS.some(option => option.value === selectedLanguage);
    setDictationLanguage(isSupportedLanguage && selectedLanguage ? selectedLanguage : DEFAULT_DICTATION_LANGUAGE);
  }, [selectedConversation?.id, selectedConversation?.dictation_language]);

  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;
    
    const recognition = new SpeechRecognition();
    recognition.lang = dictationLanguage || DEFAULT_DICTATION_LANGUAGE;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    
    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      const currentWords = message.trim() ? message.trim().split(/\s+/) : [];
      const newWords = transcript.trim().split(/\s+/);
      
      if (currentWords.length + newWords.length <= 500) {
        setMessage((message + ' ' + transcript).trim());
        setError('');
      } else {
        setError('Maximum of 500 words reached.');
        recognition.stop();
      }
    };
    
    recognition.onend = () => setIsRecording(false);
    recognitionRef.current = recognition;
  }, [message, dictationLanguage]);

  useEffect(() => {
    latestMessageRef.current = message;
  }, [message]);

  useEffect(() => {
    latestFilesRef.current = files;
  }, [files]);

  useEffect(() => {
    latestSendingRef.current = sending;
  }, [sending]);

  // Keep a stable pointer to the currently displayed conversation for async send callbacks.
  useEffect(() => {
    const previousConversationId = previousConversationIdRef.current;
    const previousKey = getDraftKey(previousConversationId);
    const currentKey = getDraftKey(conversationId);

    // Persist unsent draft of the previous conversation before switching.
    if (previousConversationId !== conversationId) {
      const previousMessage = latestMessageRef.current;
      const previousFiles = latestFilesRef.current;
      const isPreviousSending = latestSendingRef.current;
      const hasUnsentDraft = !isPreviousSending && (
        previousMessage.trim().length > 0 || previousFiles.length > 0
      );

      if (hasUnsentDraft) {
        unsentDraftsRef.current.set(previousKey, {
          message: previousMessage,
          files: [...previousFiles],
        });
      } else {
        unsentDraftsRef.current.delete(previousKey);
      }
    }

    currentConversationIdRef.current = conversationId;

    // Conversation context changed: reset local composer state to avoid stale locks/files.
    setSending(false);
    setError('');

    const currentDraft = unsentDraftsRef.current.get(currentKey);
    if (currentDraft) {
      setMessage(currentDraft.message);
      setFiles([...currentDraft.files]);
    } else {
      setMessage('');
      setFiles([]);
    }

    previousConversationIdRef.current = conversationId;
  }, [conversationId, setMessage]);

  const validateFile = (file: File): string | null => {
    // Check file extension
    const fileName = file.name.toLowerCase();
    const extension = fileName.substring(fileName.lastIndexOf('.'));
    
    if (!ALL_ALLOWED_TYPES.includes(extension)) {
      return `Invalid file type: ${extension}. Allowed types: PDF, DOCX, DOC, TXT, PNG, JPG, JPEG, GIF, WEBP`;
    }

    if (!allowedFileTypesForSelectedModel.includes(extension)) {
      return `Model ${getModelName(selectedModel)} does not support image inputs. Please switch to a multimodal model.`;
    }

    // Determine if it's an image or document
    const isImage = ALLOWED_FILE_TYPES.images.includes(extension);
    const maxSize = isImage ? MAX_FILE_SIZE.images : MAX_FILE_SIZE.documents;
    const fileType = isImage ? 'image' : 'document';
    
    // Check file size
    if (file.size > maxSize) {
      const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(0);
      return `File too large: ${(file.size / (1024 * 1024)).toFixed(1)}MB. Maximum size for ${fileType}s: ${maxSizeMB}MB`;
    }

    return null;
  };

  const handleFilesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const selected = Array.from(e.target.files);
    let newFiles = [...files];
    let errors: string[] = [];

    // Check if adding these would exceed the max number of files
    if (newFiles.length + selected.length > MAX_FILES) {
      setError(MESSAGE_ATTACHMENT_LIMITS_TEXT);
      e.target.value = '';
      return;
    }

    // Validate each file
    for (const file of selected) {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
      } else {
        const projectedTotalSize = getTotalFileSize(newFiles) + file.size;
        if (projectedTotalSize > MAX_TOTAL_FILE_SIZE) {
          errors.push(`${file.name}: ${MESSAGE_ATTACHMENT_LIMITS_TEXT}`);
          continue;
        }
        newFiles.push(file);
      }
    }

    if (errors.length > 0) {
      setError(errors.join('\n'));
    } else {
      setError('');
    }

    setFiles(newFiles);
    e.target.value = '';
  };

  const handleDeleteFile = (fileToRemove: File) => {
    setFiles(files.filter(f => f !== fileToRemove));
    setError('');
  };

  // Function to handle files dropped from parent component
  const handleDroppedFilesFromParent = (droppedFiles: File[]) => {
    let newFiles = [...files];
    let errors: string[] = [];

    // Check if adding these would exceed the max number of files
    if (newFiles.length + droppedFiles.length > MAX_FILES) {
      setError(MESSAGE_ATTACHMENT_LIMITS_TEXT);
      return;
    }

    // Validate each file
    for (const file of droppedFiles) {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
      } else {
        const projectedTotalSize = getTotalFileSize(newFiles) + file.size;
        if (projectedTotalSize > MAX_TOTAL_FILE_SIZE) {
          errors.push(`${file.name}: ${MESSAGE_ATTACHMENT_LIMITS_TEXT}`);
          continue;
        }
        newFiles.push(file);
      }
    }

    if (errors.length > 0) {
      setError(errors.join('\n'));
    } else {
      setError('');
    }

    setFiles(newFiles);
  };

  // Expose handleDroppedFiles method to parent via ref
  useImperativeHandle(ref, () => ({
    handleDroppedFiles: handleDroppedFilesFromParent
  }));

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (disabled || sending) return;
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Only set to false if we're leaving the drop zone entirely
    if (e.currentTarget === e.target) {
      setIsDragging(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled || sending) return;

    const droppedFiles = Array.from(e.dataTransfer.files);
    let newFiles = [...files];
    let errors: string[] = [];

    // Check if adding these would exceed the max number of files
    if (newFiles.length + droppedFiles.length > MAX_FILES) {
      setError(MESSAGE_ATTACHMENT_LIMITS_TEXT);
      return;
    }

    // Validate each file
    for (const file of droppedFiles) {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
      } else {
        const projectedTotalSize = getTotalFileSize(newFiles) + file.size;
        if (projectedTotalSize > MAX_TOTAL_FILE_SIZE) {
          errors.push(`${file.name}: ${MESSAGE_ATTACHMENT_LIMITS_TEXT}`);
          continue;
        }
        newFiles.push(file);
      }
    }

    if (errors.length > 0) {
      setError(errors.join('\n'));
    } else {
      setError('');
    }

    setFiles(newFiles);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || sending || disabled) return;
    
    // Clear any previous error message when attempting to send
    setError('');
    
    const messageToSend = message;
    const filesToSend = [...files];
    const submittedConversationId = conversationId;
    const submittedConversationKey = getDraftKey(submittedConversationId);
    const hasExistingConversation = Boolean(submittedConversationId);

    // Message is being sent now — this is no longer an unsent draft.
    unsentDraftsRef.current.delete(submittedConversationKey);
    
    setSending(true);
    setMessage('');
    setFiles([]);
    
    Promise.resolve(onBeforeSend(messageToSend, filesToSend))
      .then(async () => {
        if (!hasExistingConversation) {
          return;
        }

        // After sending the first message, update the conversation list.
        if (!messages || messages.length === 0) {
          const updatedConversations = await listConversations();
          setConversationList(updatedConversations);
        }

        onMessageSent(submittedConversationId);
      })
      .catch((error) => {
        console.error('Failed to send message:', error);
        // Restore draft only if user is still in the same conversation context.
        if (currentConversationIdRef.current === submittedConversationId) {
          const readableError = error instanceof Error && error.message
            ? error.message
            : 'Failed to send message. Please try again.';
          setError(readableError);
          setMessage(messageToSend);
          setFiles(filesToSend);
          unsentDraftsRef.current.set(submittedConversationKey, {
            message: messageToSend,
            files: [...filesToSend],
          });
        }
      })
      .finally(() => {
        if (currentConversationIdRef.current === submittedConversationId) {
          setSending(false);
        }
      });
  };

  // Add message change handler to clear error
  const handleMessageChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setMessage(e.target.value);
    setError(''); // Clear error when user starts typing
  };

  const handleMicClick = () => {
    if (!recognitionRef.current) {
      setError('Speech Recognition not supported in this browser.');
      return;
    }
    if (isRecording) {
      recognitionRef.current.stop();
    } else {
      try {
        recognitionRef.current.start();
        setIsRecording(true);
        setError('');
      } catch {
        setIsRecording(false);
        setError(`Unable to start dictation for ${dictationLanguage}. Falling back to ${DEFAULT_DICTATION_LANGUAGE}.`);
      }
    }
  };

  const handleDictationMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setDictationMenuAnchorEl(event.currentTarget);
  };

  const handleDictationMenuClose = () => {
    setDictationMenuAnchorEl(null);
  };

  const handleDictationLanguageChange = async (language: DictationLanguage) => {
    setDictationLanguage(language);
    setDictationMenuAnchorEl(null);

    if (!selectedConversation?.id) {
      return;
    }

    try {
      const updatedConversation = await updateConversation(selectedConversation.id, {
        dictation_language: language,
      });

      setSelectedConversation(updatedConversation);
      setConversationList(
        conversationList.map(conv => (conv.id === updatedConversation.id ? updatedConversation : conv))
      );
    } catch (persistError) {
      console.error('Failed to persist dictation language:', persistError);
      setError('Could not save dictation language for this conversation.');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Handle paste events for file attachments
  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    const items = e.clipboardData?.items;
    if (!items) return;

    let newFiles = [...files];
    let errors: string[] = [];
    let pastedFiles: File[] = [];

    // Extract files from clipboard
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      
      // Check if it's a file
      if (item.kind === 'file') {
        const file = item.getAsFile();
        if (file) {
          pastedFiles.push(file);
        }
      }
    }

    if (pastedFiles.length === 0) return;

    // Check if adding these would exceed the max number of files
    if (newFiles.length + pastedFiles.length > MAX_FILES) {
      setError(MESSAGE_ATTACHMENT_LIMITS_TEXT);
      return;
    }

    // Validate each pasted file
    for (const file of pastedFiles) {
      const validationError = validateFile(file);
      if (validationError) {
        errors.push(`${file.name}: ${validationError}`);
      } else {
        const projectedTotalSize = getTotalFileSize(newFiles) + file.size;
        if (projectedTotalSize > MAX_TOTAL_FILE_SIZE) {
          errors.push(`${file.name}: ${MESSAGE_ATTACHMENT_LIMITS_TEXT}`);
          continue;
        }
        newFiles.push(file);
      }
    }

    if (errors.length > 0) {
      setError(errors.join('\n'));
    } else {
      setError('');
    }

    setFiles(newFiles);
  };

  // Model selection handling
  const handleModelChange = (modelId: string) => {

    setSelectedModel(modelId);
    handleModelMenuClose();
  };
  
  // Model menu handling
  const handleModelMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setModelMenuAnchorEl(event.currentTarget);
  };

  const handleModelMenuClose = () => {
    setModelMenuAnchorEl(null);
  };

  // Fetch accessible models (implicit + explicit permissions) so we can show names
  useEffect(() => {
    const fetchAccessibleModels = async () => {
      try {
        // Use the accessible endpoint which returns implicit + permissioned models for the current user
        const models = await modelsAPI.getAccessibleModels();

        setAllModels(models.map((m: any) => ({ id: m.id, name: m.name, supports_images: !!m.supports_images })));

        // If modelList is empty (user settings not yet populated), seed it with accessible models
        if (modelList.length === 0 && models.length > 0) {
          const modelOptions = models.map((m: any) => ({ id: m.id, name: m.name, supports_images: !!m.supports_images }));
          useAppStore.setState({ modelList: modelOptions });
        }
      } catch (error) {
        console.error('Failed to fetch accessible models:', error);
        // Fallback to implicit models only
        try {
          const implicit = await modelsAPI.getImplicitModels();
          setAllModels(implicit.map((m: any) => ({ id: m.id, name: m.name, supports_images: !!m.supports_images })));
          if (modelList.length === 0 && implicit.length > 0) {
            useAppStore.setState({ modelList: implicit.map((m: any) => ({ id: m.id, name: m.name, supports_images: !!m.supports_images })) });
          }
        } catch (err) {
          console.error('Failed to fetch implicit models as fallback:', err);
        }
      }
    };
    fetchAccessibleModels();
  }, [modelList.length]);

  // Set first model as default when modelList changes
  useEffect(() => {
    if (modelList.length > 0) {
      // If no model is selected, select the first one
      if (!selectedModel) {
        setSelectedModel(modelList[0].id);
      } else {
        // Verify that the selected model still exists in the modelList
        const modelExists = modelList.some(m => m.id === selectedModel);
        if (!modelExists) {
          // If saved model no longer exists, fall back to first model
          setSelectedModel(modelList[0].id);
        }
      }
    }
  }, [modelList, selectedModel, setSelectedModel]);

  // Get model display name
  const getModelName = (modelId: string) => {
    // Prefer the global modelList (may be seeded from user settings)
    const fromList = modelList.find(m => m.id === modelId);
    if (fromList && fromList.name) return fromList.name;

    // Then check allModels (fetched accessible/implicit models)
    const model = allModels.find(m => m.id === modelId);
    if (model && model.name) return model.name;

    // Fallback to the raw id
    return modelId;
  };

  // Get model description
  const getModelDescription = (modelId: string): string | null => {
    const model = modelList.find(m => m.id === modelId);
    return model?.description || null;
  };

  useEffect(() => {
    if (selectedModelSupportsImages) {
      return;
    }

    const imageExtensions = new Set(ALLOWED_FILE_TYPES.images);
    const hasImageAttachments = files.some(file => imageExtensions.has(file.name.toLowerCase().substring(file.name.lastIndexOf('.'))));
    if (!hasImageAttachments) {
      return;
    }

    const filteredFiles = files.filter(file => !imageExtensions.has(file.name.toLowerCase().substring(file.name.lastIndexOf('.'))));
    setFiles(filteredFiles);
    setError(`Removed image attachments because ${getModelName(selectedModel)} does not support image input.`);
  }, [files, selectedModel, selectedModelSupportsImages]);

  // Handle model name click - open description dialog
  const handleModelNameClick = () => {
    setModelToShowDescription(selectedModel);
    setDescriptionDialogOpen(true);
  };

  // Handle info icon click - open description dialog for specific model
  const handleInfoIconClick = (modelId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent model selection
    setModelToShowDescription(modelId);
    setDescriptionDialogOpen(true);
  };

  const handleDescriptionDialogClose = () => {
    setDescriptionDialogOpen(false);
    setModelToShowDescription(null);
  };

  // Sort models alphabetically by name
  const sortedModels = [...modelList].sort((a, b) => {
    const nameA = getModelName(a.id);
    const nameB = getModelName(b.id);
    return nameA.localeCompare(nameB);
  });

  const inputAuxiliaryWidthSx = {
    maxWidth: '800px',
    width: '100%',
    margin: '0 auto',
  } as const;

  const visibleFiles = isSmallDevice ? files.slice(0, 2) : files;
  const hiddenFileCount = isSmallDevice ? Math.max(files.length - visibleFiles.length, 0) : 0;

  const IMAGE_EXTENSIONS = new Set(['.png', '.jpg', '.jpeg', '.gif', '.webp']);
  const isImageFile = (file: File) => IMAGE_EXTENSIONS.has(file.name.toLowerCase().substring(file.name.lastIndexOf('.')));
  const filePreviewKey = (file: File) => `${file.name}-${file.size}`;

  useEffect(() => {
    const newUrls = new Map<string, string>();
    files.forEach(file => {
      if (isImageFile(file)) {
        const key = filePreviewKey(file);
        // Reuse existing URL if already created to avoid unnecessary revocations
        const existing = imagePreviewUrls.get(key);
        newUrls.set(key, existing ?? URL.createObjectURL(file));
      }
    });
    // Revoke URLs that are no longer needed
    imagePreviewUrls.forEach((url, key) => {
      if (!newUrls.has(key)) URL.revokeObjectURL(url);
    });
    setImagePreviewUrls(newUrls);
    return () => {
      newUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [files]); // eslint-disable-line react-hooks/exhaustive-deps

  const getFileChipLabel = (file: File) => {
    if (isSmallDevice) {
      return file.name;
    }

    return `${file.name} (${(file.size / 1024).toFixed(1)} KB)`;
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      {files.length > 0 && (
        <>
          {/* OneDrive upload notification banner */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            backgroundColor: theme.palette.mode === 'light' ? '#f5f5f5' : '#2a2a2a',
            borderRadius: '8px 8px 0 0',
            px: 2,
            py: 1,
            ...inputAuxiliaryWidthSx,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                component="span"
                sx={{
                  width: 16,
                  height: 16,
                  borderRadius: '50%',
                  backgroundColor: theme.palette.info.main,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: 'bold',
                }}
              >
                i
              </Box>
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                Uploading a file will send a copy to your Bossard OneDrive.{' '}
                <Box
                  component="a"
                  href={getOneDriveFolderUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    color: theme.palette.primary.main,
                    textDecoration: 'none',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Manage uploads
                </Box>
              </Typography>
            </Box>
            <IconButton
              size="small"
              onClick={() => setFiles([])}
              sx={{ color: theme.palette.text.secondary }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {/* File chips */}
          <Box sx={{ 
            display: 'flex', 
            flexWrap: 'wrap', 
            p: 1, 
            ...inputAuxiliaryWidthSx,
            backgroundColor: theme.palette.mode === 'light' ? '#fafafa' : '#1e1e1e',
            borderRadius: '0 0 8px 8px',
            mb: 1,
            maxHeight: { xs: 88, sm: 'none' },
            overflowY: { xs: 'auto', sm: 'visible' },
          }}>
            {visibleFiles.map(file => {
              const previewUrl = imagePreviewUrls.get(filePreviewKey(file));
              const chip = (
                <Chip
                  key={`${file.name}-${file.size}`}
                  label={getFileChipLabel(file)}
                  icon={<AttachFile fontSize="small" />}
                  onDelete={() => handleDeleteFile(file)}
                  size={isSmallDevice ? 'small' : 'medium'}
                  sx={{
                    mr: 1,
                    mb: 0.5,
                    mt: 0.5,
                    maxWidth: '100%',
                    '& .MuiChip-label': {
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: { xs: '0.72rem', sm: '0.8125rem' },
                      maxWidth: { xs: '136px', sm: '360px', md: '480px' },
                    },
                  }}
                />
              );
              if (previewUrl) {
                return (
                  <Tooltip
                    key={`${file.name}-${file.size}`}
                    title={
                      <Box
                        component="img"
                        src={previewUrl}
                        alt={file.name}
                        sx={{ maxWidth: 200, maxHeight: 200, borderRadius: 1, display: 'block' }}
                      />
                    }
                    placement="top"
                    arrow
                    componentsProps={{ tooltip: { sx: { bgcolor: 'transparent', p: 0.5 } }, arrow: { sx: { color: theme.palette.grey[800] } } }}
                  >
                    {chip}
                  </Tooltip>
                );
              }
              return chip;
            })}
            {hiddenFileCount > 0 && (
              <Chip
                label={`+${hiddenFileCount}`}
                size="small"
                sx={{ mr: 1, mb: 0.5, mt: 0.5 }}
              />
            )}
          </Box>
        </>
      )}
      {error && (
        <Typography color="error" variant="body2" sx={{ mb: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' }, ...inputAuxiliaryWidthSx }}>
          {error}
        </Typography>
      )}
      <FloatingInput 
        elevation={4}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        sx={{
          position: 'relative',
          border: isDragging ? `2px dashed ${theme.palette.primary.main}` : undefined,
          backgroundColor: isDragging 
            ? theme.palette.mode === 'light' 
              ? 'rgba(0, 122, 195, 0.05)' 
              : 'rgba(0, 122, 195, 0.1)'
            : undefined,
          transition: 'all 0.2s ease-in-out'
        }}
      >
        {/* Drag overlay */}
        {isDragging && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: theme.palette.mode === 'light'
                ? 'rgba(255, 255, 255, 0.9)'
                : 'rgba(0, 0, 0, 0.7)',
              borderRadius: '12px',
              zIndex: 1,
              pointerEvents: 'none'
            }}
          >
            <Box sx={{ textAlign: 'center' }}>
              <AttachFile sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" color="primary.main" fontWeight={600}>
                Drop files here
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedModelSupportsImages
                  ? `Maximum ${MAX_FILES} files (PDF, DOCX, DOC, TXT, PNG, JPG, JPEG, GIF, WEBP)`
                  : `Maximum ${MAX_FILES} files (PDF, DOCX, DOC, TXT)`}
              </Typography>
            </Box>
          </Box>
        )}
        {/* Input Area */}
        <Box sx={{ 
          display: 'flex', 
          width: '100%', 
          alignItems: 'flex-start',
          flex: 1
        }}>
          <Box
            ref={textareaRef}
            component="textarea"
            onKeyUp={handleKeyPress}
            onPaste={handlePaste}
            spellCheck={false}
            sx={{
              flex: 1,
              border: 'none',
              outline: 'none',
              resize: 'none',
              fontSize: { xs: 14, sm: 16 },
              fontFamily: 'Arial, Inter, sans-serif',
              backgroundColor: 'transparent',
              minHeight: '20px',
              maxHeight: { xs: '120px', sm: '200px' },
              width: '100%',
              paddingY: { xs: 0.25, sm: 0.5 },
              overflow: 'auto',
              color: theme.palette.mode === 'light' ? theme.palette.text.primary : 'rgba(255, 255, 255, 0.9)',
              '&::placeholder': {
                color: theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.5)',
                fontSize: { xs: 13, sm: 16 },
              }
            }}
            placeholder={files.length > 0 
              ? "Ask a question about your document(s)..." 
              : "Type your message here..."}
            value={message}
            onChange={handleMessageChange}
            disabled={sending || disabled}
          />
        </Box>
        
        {/* Bottom Controls */}
        <Box sx={{ 
          display: 'flex', 
          width: '100%', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          flexWrap: useCompactControlRow ? 'nowrap' : 'wrap',
          rowGap: 0.75,
          columnGap: 1,
          borderTop: `1px solid ${theme.palette.mode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)'}`,
          paddingTop: { xs: 0.5, sm: 1 },
          mt: 1
        }}>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: useCompactControlRow ? 'nowrap' : 'wrap',
            gap: 0.5,
            flex: '1 1 260px',
            minWidth: 0,
            overflowX: useCompactControlRow ? 'auto' : 'visible',
            overflowY: 'hidden',
            '&::-webkit-scrollbar': {
              display: useCompactControlRow ? 'none' : 'initial',
            },
          }}>
            {/* Attachment Button */}
            <Tooltip
              title={selectedModelSupportsImages ? 'Attach files' : 'Attach documents only (current model has no image support)'}
              placement="top"
              arrow
            >
              <IconButton 
                color="primary" 
                component="label" 
                disabled={sending || disabled || files.length >= MAX_FILES}
                sx={{ position: 'relative', transition: 'transform 0.2s', '&:hover': { bgcolor: theme.palette.mode === 'light' ? 'rgba(0, 122, 195, 0.08)' : 'rgba(0, 122, 195, 0.16)', transform: 'translateY(-2px)' } }}
              >
                <AttachFile />
              <input 
                type="file" 
                hidden 
                multiple 
                onChange={handleFilesChange} 
                accept={allowedFileTypesForSelectedModel.join(',')}
              />
              {files.length >= MAX_FILES && (
                <Box sx={{ 
                  position: 'absolute', 
                  top: -8, 
                  right: -8, 
                  bgcolor: 'error.main', 
                  borderRadius: '50%', 
                  width: 16, 
                  height: 16, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  <Typography variant="caption" sx={{ color: 'white', fontSize: '10px' }}>
                    {MAX_FILES}
                  </Typography>
                </Box>
              )}
              </IconButton>
            </Tooltip>
            
            {/* Dictation Control (integrated mic + language selector) */}
            <ButtonGroup
              variant="outlined"
              size="small"
              disabled={sending || disabled}
              sx={{
                ml: 0.5,
                borderRadius: '20px',
                overflow: 'seen',
                '& .MuiButton-root': {
                  textTransform: 'none',
                  borderColor: theme.palette.mode === 'light' ? 'rgba(0, 122, 195, 0.35)' : 'rgba(0, 122, 195, 0.45)',
                  color: isRecording ? 'error.main' : 'primary.main',
                },
                '& .MuiButton-root:hover': {
                  borderColor: 'primary.main',
                },
              }}
            >
              <Tooltip title="Start/stop dictation" placement="top" arrow>
                <Button
                  type="button"
                  onClick={handleMicClick}
                  startIcon={<Mic color={isRecording ? 'error' : 'inherit'} />}
                  sx={{
                    pl: 1,
                    pr: 1,
                    minWidth: 0,
                    fontSize: '0.7rem',
                    fontWeight: 600,
                  }}
                >
                  {dictationLanguage}
                </Button>
              </Tooltip>
              <Tooltip title="Select dictation language" placement="top" arrow>
                <Button
                  aria-label="Select dictation language"
                  onClick={handleDictationMenuOpen}
                  sx={{ minWidth: 30, px: 0.5 }}
                >
                  <ArrowDropDown fontSize="small" />
                </Button>
              </Tooltip>
            </ButtonGroup>

            <Menu
              anchorEl={dictationMenuAnchorEl}
              open={Boolean(dictationMenuAnchorEl)}
              onClose={handleDictationMenuClose}
            >
              {DICTATION_LANGUAGE_OPTIONS.map(option => (
                <MenuItem
                  key={option.value}
                  selected={dictationLanguage === option.value}
                  onClick={() => handleDictationLanguageChange(option.value)}
                >
                  {option.label}
                </MenuItem>
              ))}
            </Menu>

            {/* Data Source Toggles */}
            <SourceToggles disabled={sending || disabled} compact={useCompactControlRow} />

              {/* Model Selection + Thinking Mode — hidden for Researcher agent */}
              {!isResearcher && (
              <>
              {/* Model Selection */}
            <Box sx={{ display: 'flex', alignItems: 'center', ml: useCompactControlRow ? 0.5 : 2, flexShrink: 0 }}>
              <Tooltip
                title="Select a model"
                placement="top"
                arrow
              >
                <IconButton
                  onClick={handleModelMenuClick}
                  sx={{ 
                    color: 'primary.main',
                    transition: 'transform 0.2s',
                    '&:hover': { bgcolor: theme.palette.mode === 'light' ? 'rgba(0, 122, 195, 0.08)' : 'rgba(0, 122, 195, 0.16)', transform: 'translateY(-2px)' }
                  }}
                  aria-label="Select model"
                >
                  <BuildCircle />
                </IconButton>
              </Tooltip>
              
              <Menu
                anchorEl={modelMenuAnchorEl}
                open={Boolean(modelMenuAnchorEl)}
                onClose={handleModelMenuClose}
                disableAutoFocusItem
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: -2,
                    minWidth: '200px',
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateX(-50%) translateY(50%) rotate(45deg)',
                      zIndex: 0,
                    },
                    '& .MuiMenuItem-root': {
                      minHeight: '40px',
                      py: 0.75,
                      px: 2,
                      fontSize: '0.95rem',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'light' 
                          ? 'rgba(0, 0, 0, 0.04)' 
                          : 'rgba(255, 255, 255, 0.08)'
                      },
                      '&.Mui-selected': {
                        bgcolor: theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.08)'
                          : 'rgba(255, 255, 255, 0.12)',
                        '&:hover': {
                          bgcolor: theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.12)'
                            : 'rgba(255, 255, 255, 0.16)'
                        }
                      }
                    }
                  }
                }}
                MenuListProps={{
                  disablePadding: false,
                }}
              >
                {sortedModels.length === 0 ? (
                  <MenuItem disabled>Loading models...</MenuItem>
                ) : (
                  <>
                    {sortedModels.map((model) => (
                      <MenuItem 
                        key={model.id} 
                        selected={model.id === selectedModel}
                        onClick={() => {  handleModelChange(model.id)}}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center'
                        }}
                      >
                        <span>{getModelName(model.id)}</span>
                        {getModelDescription(model.id) && (
                          <InfoOutlined 
                            sx={{ 
                              fontSize: '18px',
                              ml: 1,
                              color: theme.palette.mode === 'light' 
                                ? 'rgba(0, 0, 0, 0.54)' 
                                : 'rgba(255, 255, 255, 0.54)',
                              cursor: 'pointer',
                              '&:hover': {
                                color: theme.palette.primary.main
                              }
                            }}
                            onClick={(e) => handleInfoIconClick(model.id, e)}
                          />
                        )}
                      </MenuItem>
                    ))}
                    
                    {/* Divider before "Request other" */}
                    <Divider sx={{ my: 0.5 }} />
                    
                    {/* "Request other" option */}
                    <MenuItem 
                      onClick={() => {
                        handleModelMenuClose();
                        navigate('/user-settings', { state: { tab: 1 } });
                      }}
                      sx={{
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        fontStyle: 'italic'
                      }}
                    >
                      Request other
                    </MenuItem>
                  </>
                )}
                
              </Menu>
              
              {!useCompactControlRow && (
                <Tooltip 
                  title="Click to read model description"
                  placement="top"
                  arrow
                  enterDelay={500}
                  leaveDelay={200}
                >
                  <Typography 
                    variant="body2" 
                    onClick={handleModelNameClick}
                    sx={{ 
                      ml: 1, 
                      color: theme.palette.mode === 'light' ? theme.palette.text.secondary : 'rgba(255, 255, 255, 0.7)',
                      maxWidth: { xs: 76, sm: 120 },
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      cursor: 'pointer',
                      '&:hover': {
                        textDecoration: 'underline',
                        textDecorationStyle: 'dotted'
                      }
                    }}
                  >
                    {selectedModel ? getModelName(selectedModel) : 'Select model'}
                  </Typography>
                </Tooltip>
              )}
              
              {/* Model Description Dialog */}
              <DialogWrapper
                open={descriptionDialogOpen}
                onClose={handleDescriptionDialogClose}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>
                  {modelToShowDescription ? getModelName(modelToShowDescription) : 'Model'}
                  <IconButton
                    aria-label="close"
                    onClick={handleDescriptionDialogClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                  >
                    <CloseIcon />
                  </IconButton>
                </DialogTitle>
                <DialogContent>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6, mt: 1 }}>
                    {modelToShowDescription ? (getModelDescription(modelToShowDescription) || 'No description available for this model.') : ''}
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleDescriptionDialogClose} color="primary" variant="contained">
                    Close
                  </Button>
                </DialogActions>
              </DialogWrapper>
            </Box>

            {canToggleMoreControls && !showMoreControls && (
              <Chip
                label="More"
                size="small"
                clickable
                onClick={() => setShowMoreControls(true)}
                sx={{ ml: 0.5, height: 24, fontSize: '0.72rem', flexShrink: 0 }}
              />
            )}

            {/* Thinking Mode Selector */}
            {(!useCompactControlRow || showMoreControls) && (
            <Box sx={{ display: 'flex', alignItems: 'center', ml: useCompactControlRow ? 0.5 : 2, flexShrink: 0 }}>
              <Tooltip
                title="Thinking mode"
                placement="top"
                arrow
              >
                <IconButton
                  onClick={(e) => setThinkingMenuAnchorEl(e.currentTarget)}
                  sx={{ 
                    color: thinkingMode === 'deep' ? 'warning.main' : thinkingMode === 'quick' ? 'success.main' : 'primary.main',
                    transition: 'transform 0.2s',
                    '&:hover': { bgcolor: theme.palette.mode === 'light' ? 'rgba(0, 122, 195, 0.08)' : 'rgba(0, 122, 195, 0.16)', transform: 'translateY(-2px)' }
                  }}
                  aria-label="Select thinking mode"
                >
                  <Psychology />
                </IconButton>
              </Tooltip>
              
              <Menu
                anchorEl={thinkingMenuAnchorEl}
                open={Boolean(thinkingMenuAnchorEl)}
                onClose={() => setThinkingMenuAnchorEl(null)}
                disableAutoFocusItem
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'center',
                }}
                transformOrigin={{
                  vertical: 'bottom',
                  horizontal: 'center',
                }}
                PaperProps={{
                  elevation: 0,
                  sx: {
                    overflow: 'visible',
                    filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                    mt: -2,
                    minWidth: '160px',
                    '&:before': {
                      content: '""',
                      display: 'block',
                      position: 'absolute',
                      bottom: 0,
                      left: '50%',
                      width: 10,
                      height: 10,
                      bgcolor: 'background.paper',
                      transform: 'translateX(-50%) translateY(50%) rotate(45deg)',
                      zIndex: 0,
                    },
                    '& .MuiMenuItem-root': {
                      minHeight: '40px',
                      py: 0.75,
                      px: 2,
                      fontSize: '0.95rem',
                      '&:hover': {
                        bgcolor: theme.palette.mode === 'light' 
                          ? 'rgba(0, 0, 0, 0.04)' 
                          : 'rgba(255, 255, 255, 0.08)'
                      },
                      '&.Mui-selected': {
                        bgcolor: theme.palette.mode === 'light'
                          ? 'rgba(0, 0, 0, 0.08)'
                          : 'rgba(255, 255, 255, 0.12)',
                        '&:hover': {
                          bgcolor: theme.palette.mode === 'light'
                            ? 'rgba(0, 0, 0, 0.12)'
                            : 'rgba(255, 255, 255, 0.16)'
                        }
                      }
                    }
                  }
                }}
                MenuListProps={{
                  disablePadding: false,
                }}
              >
                <MenuItem
                  selected={thinkingMode === 'auto'}
                  onClick={() => { setThinkingMode('auto'); setThinkingMenuAnchorEl(null); }}
                >
                  Auto
                </MenuItem>
                <MenuItem
                  selected={thinkingMode === 'quick'}
                  onClick={() => { setThinkingMode('quick'); setThinkingMenuAnchorEl(null); }}
                >
                  ⚡ Quick
                </MenuItem>
                <MenuItem
                  selected={thinkingMode === 'deep'}
                  onClick={() => { setThinkingMode('deep'); setThinkingMenuAnchorEl(null); }}
                >
                  🧠 Think deeper
                </MenuItem>
              </Menu>
              
              {!useCompactControlRow ? (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    ml: 1, 
                    color: theme.palette.mode === 'light' ? theme.palette.text.secondary : 'rgba(255, 255, 255, 0.7)',
                    maxWidth: { xs: 68, sm: 90 },
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {thinkingMode === 'deep' ? '🧠 Deeper' : thinkingMode === 'quick' ? '⚡ Quick' : 'Auto'}
                </Typography>
              ) : null}
            </Box>
            )}

            {canToggleMoreControls && showMoreControls && (
              <Chip
                label="Less"
                size="small"
                clickable
                onClick={() => setShowMoreControls(false)}
                sx={{ ml: 0.5, height: 24, fontSize: '0.72rem', flexShrink: 0 }}
              />
            )}
            </> )}  {/* end !isResearcher guard */}
          </Box>
          
          {/* Send Button */}
          <IconButton 
            type="submit" 
            color="primary" 
            disabled={((!message.trim() && files.length === 0) || sending || disabled)}
            sx={{ ml: 'auto', transition: 'transform 0.2s', '&:hover': { bgcolor: theme.palette.mode === 'light' ? 'rgba(0, 122, 195, 0.08)' : 'rgba(0, 122, 195, 0.16)', transform: 'translateY(-2px)' } }}
          >
            {sending ? <CircularProgress size={24} /> : <Send />}
          </IconButton>
        </Box>
      </FloatingInput>
    </Box>
  );
});

ChatInput.displayName = 'ChatInput';
